import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { Pill } from '../../components/ui';
import { dateLabel, num, customerShort } from '../../lib/format';

// 生产业务模型收口 #2：客户送修工件 = 实时在厂台账
// 唯一事实来源：physical_assets + asset_service_cycles + asset_movements
// 数量/状态全部只读；收件走 /api/physical-assets/intake；交货/退件走正式业务动作

export type WorkpieceLedgerRow = {
  asset_id: string;
  customer_serial: string;
  asset_type?: string;
  customer_code?: string;
  customer_name?: string;
  service_cycle_id: string;
  version: number;
  cycle_status?: string;
  intake_date?: string;
  findings?: string;
  recommendation?: string;
  in_factory_qty: number;
  received_qty: number;
  delivered_qty: number;
  returned_qty: number;
};

function inFactoryPill(qty: number) {
  if (qty > 0) return <Pill tone="ok" label="在厂" />;
  return <Pill tone="neutral" label="已离厂" />;
}

function cycleStatusPill(s?: string) {
  const map: Record<string, { tone: 'ok' | 'warn' | 'neutral' | 'bad'; label: string }> = {
    intake: { tone: 'ok', label: '已收件' },
    in_production: { tone: 'warn', label: '加工中' },
    inspected: { tone: 'warn', label: '待检' },
    ready: { tone: 'ok', label: '待交付' },
    delivered: { tone: 'neutral', label: '已交货' },
    returned: { tone: 'neutral', label: '已退件' },
    closed: { tone: 'neutral', label: '已关闭' },
  };
  const m = map[s || ''] || { tone: 'neutral' as const, label: s || '—' };
  return <Pill tone={m.tone} label={m.label} />;
}

export function WorkpieceLedgerPage() {
  const [sp] = useSearchParams();
  const [reloadSignal] = useState(0);

  const columns: Column<WorkpieceLedgerRow>[] = [
    { header: '客户', render: (r) => <span className="strong" title={r.customer_name || r.customer_code || ''}>{customerShort(r.customer_name || r.customer_code)}</span> },
    { header: '工件序列号', render: (r) => <span className="num">{r.customer_serial || '—'}</span> },
    { header: '工件类型', render: (r) => r.asset_type || '—' },
    { header: '维修版本', render: (r) => <span className="num">V{r.version}</span> },
    { header: '到货日期', render: (r) => <span className="num">{dateLabel(r.intake_date)}</span> },
    // 在厂数 = 数量；状态 = 是否在厂（在厂 / 已离厂）
    { header: '在厂数', align: 'right', render: (r) => <span className="num strong">{num(r.in_factory_qty, 0)}</span> },
    { header: '收件', align: 'right', render: (r) => <span className="num">{num(r.received_qty, 0)}</span> },
    { header: '已交货', align: 'right', render: (r) => <span className="num">{num(r.delivered_qty, 0)}</span> },
    { header: '已退件', align: 'right', render: (r) => <span className="num">{num(r.returned_qty, 0)}</span> },
    { header: '状态', render: (r) => inFactoryPill(Number(r.in_factory_qty || 0)) },
    { header: '履历', render: (r) => cycleStatusPill(r.cycle_status) },
  ];

  return (
    <ListPage<WorkpieceLedgerRow>
      title="客户送修工件 · 实时在厂台账"
      desc="唯一事实来源：physical_assets + asset_service_cycles + asset_movements。数量/状态只读，在厂数 = 收件 − 交货 − 退件（由流水实时计算）。收件走正式登记，交货/退件走正式业务动作，不再独立改状态。"
      endpoint="/api/workpiece-ledger"
      columns={columns}
      rowId={(r) => `${r.asset_id}:${r.service_cycle_id}`}
      searchPlaceholder="搜索客户 / 序列号 / 工件类型…"
      initialQuery={sp.get('q') || ''}
      reloadSignal={reloadSignal}
      renderDetail={(row) => <WorkpieceLedgerDetail row={row} />}
    />
  );
}

function WorkpieceLedgerDetail({ row }: { row: WorkpieceLedgerRow }) {
  return (
    <div style={{ padding: '8px 4px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, margin: '8px 0' }}>
        <Field label="客户" value={row.customer_name || row.customer_code || '—'} />
        <Field label="工件序列号" value={row.customer_serial || '—'} />
        <Field label="工件类型" value={row.asset_type || '—'} />
        <Field label="维修版本" value={`V${row.version}`} />
        <Field label="到货日期" value={dateLabel(row.intake_date) || '—'} />
        <Field label="来料状态" value={row.findings || '—'} />
        <Field label="处理建议" value={row.recommendation || '—'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, margin: '12px 0', padding: '10px', background: '#f8fafc', borderRadius: 8 }}>
        <div><div style={{ color: '#94a3b8', fontSize: 11 }}>已收件</div><div style={{ fontSize: 18, fontWeight: 700 }}>{num(row.received_qty, 0)}</div></div>
        <div><div style={{ color: '#94a3b8', fontSize: 11 }}>在厂数</div><div style={{ fontSize: 18, fontWeight: 700 }}>{num(row.in_factory_qty, 0)}</div></div>
        <div><div style={{ color: '#94a3b8', fontSize: 11 }}>已交货</div><div style={{ fontSize: 18, fontWeight: 700 }}>{num(row.delivered_qty, 0)}</div></div>
        <div><div style={{ color: '#94a3b8', fontSize: 11 }}>已退件</div><div style={{ fontSize: 18, fontWeight: 700 }}>{num(row.returned_qty, 0)}</div></div>
      </div>
      <div style={{ color: '#94a3b8', fontSize: 12 }}>
        在厂数 = 收件 − 交货 − 退件（由 asset_movements 流水实时计算，只读）
      </div>

      <div style={{ marginTop: 12, padding: '10px', background: '#fff3cd', borderRadius: 8, fontSize: 13 }}>
        <strong>本页只读。</strong>收件走「收件登记」，交货/退件走「交货 / 客户工件退料」正式业务动作。
        不再支持直接改状态或独立开交还单。
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: '#94a3b8', fontSize: 11 }}>{label}</div>
      <div style={{ fontSize: 13, wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}
